import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RoleService } from '../../../core/services/role.service';
import { Role } from '../../../core/models/role.model';
import { FormBuilder, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { LoadingService } from '../../../core/services/loading.service';
import { ActionAlert } from '../../../core/dto/actionAlert';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'app-add-role',
  templateUrl: './add-role.component.html',
  styleUrls: ['./add-role.component.scss'],
})
export class AddRoleComponent implements OnInit {
  rId: any = undefined;
  isAdd = false;
  allPermissions: any = [];
  isDataLoading = false;
  selectedPermissions: any = [];
  singleRole: Role = {};
  roleForm: any;
  actionMessage = new ActionAlert();

  constructor(
    private route: ActivatedRoute,
    private roleService: RoleService,
    private fb: FormBuilder,
    private _location: Location,
    private loading: LoadingService,
    private translocoService: TranslocoService
  ) {
    this.loading.start();
  }

  ngOnInit(): void {
    // Set Add or Update Page
    this.rId = this.route.snapshot.params['id'];
    this.isAdd = !this.rId;
    Promise.all([this.loadAllPermissions()]).then(() => this.loading.stop());
    if (!this.isAdd) {
      this.loadRoleByID();
    }
    this.roleForm = this.fb.group({
      name: ['', [Validators.required]],
    });
  }

  async loadAllPermissions(): Promise<any> {
    this.isDataLoading = true;
    try {
      this.allPermissions = await this.roleService.getAllPermissions();
      this.isDataLoading = false;
    } catch (e) {
      this.allPermissions = [];
    }
  }

  async loadRoleByID(): Promise<any> {
    this.isDataLoading = true;
    try {
      this.singleRole = await this.roleService.getRoleByID(this.rId);
      this.roleForm?.patchValue(this.singleRole);
      this.selectedPermissions = this.singleRole.permissions;
      this.isDataLoading = false;
    } catch (e) {
      this._location.back();
    }
  }

  setPermissions(perms: string[]): void {
    this.selectedPermissions = perms;
  }

  isSelectedPermission(id: string): any {
    return this.singleRole.permissions?.includes(id);
  }

  _isSelectedPermissionsEmpty(): boolean {
    return this.selectedPermissions.length === 0;
  }

  async addRole(): Promise<any> {
    this.loading.start();
    const payload: Role = Object.assign(
      {},
      {
        name: this.roleForm.getRawValue().name,
        permissions: this.selectedPermissions,
      }
    );
    try {
      await this.roleService.addNewRole(payload);
      this.loadAllPermissions();
      this.roleForm.reset();
      this.actionMessage = new ActionAlert(
        true,
        this.isAdd,
        this.translocoService.translate('roles.addEdit.messages.newRoleAdded'),
        'success'
      );
      this.loading.stop();
    } catch (e: any) {
      if (e.error.type === 'exist') {
        this.actionMessage = new ActionAlert(
          true,
          this.isAdd,
          e.error.message,
          'error'
        );
      } else {
        this.actionMessage = new ActionAlert(
          true,
          this.isAdd,
          this.translocoService.translate('roles.addEdit.messages.newRoleNotAdded'),
          'error'
        );
      }

      this.loading.stop();
    }
  }

  async updateRole(): Promise<any> {
    this.loading.start();
    const payload: Role = Object.assign(
      {},
      {
        _id: this.rId,
        name: this.roleForm.getRawValue().name,
        permissions: this.selectedPermissions,
      }
    );
    try {
      await this.roleService.editRole(payload);
      this.actionMessage = new ActionAlert(
        true,
        !this.isAdd,
        this.translocoService.translate('roles.addEdit.messages.roleUpdated'),
        'success'
      );
      this.loading.stop();
    } catch (e: any) {
      if (e.error.type === 'exist') {
        this.actionMessage = new ActionAlert(
          true,
          this.isAdd,
          e.error.message,
          'error'
        );
      } else {
        this.actionMessage = new ActionAlert(
          true,
          !this.isAdd,
          this.translocoService.translate('roles.addEdit.messages.roleNotUpdated'),
          'error'
        );
      }
      this.loading.stop();
    }
  }
}
